import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

import api from './api';
import themeConfig from './theme/style';
import ErrorBoundary from './components/ErrorBoundary';

const lazyWithRetry = (componentImport) =>
  lazy(async () => {
    const pageHasAlreadyBeenForceRefreshed = JSON.parse(
      window.sessionStorage.getItem('page-has-been-force-refreshed') || 'false'
    );
    try {
      const component = await componentImport();
      window.sessionStorage.setItem('page-has-been-force-refreshed', 'false');
      return component;
    } catch (error) {
      if (!pageHasAlreadyBeenForceRefreshed && error.name === 'ChunkLoadError' || error.message.includes('Loading chunk')) {
        window.sessionStorage.setItem('page-has-been-force-refreshed', 'true');
        window.location.reload();
        return new Promise(() => {});
      }
      throw error;
    }
  });

const UserLogin = lazyWithRetry(() => import('./components/UserLogin'));
const UserForgotPassword = lazyWithRetry(() => import('./components/UserForgotPassword'));
const UserResetPassword = lazyWithRetry(() => import('./components/UserResetPassword'));
const EvaluationTaker = lazyWithRetry(() => import('./components/EvaluationTaker'));
const EvaluationComplete = lazyWithRetry(() => import('./components/EvaluationComplete'));
const Register = lazyWithRetry(() => import('./components/Register'));
const UserVerify = lazyWithRetry(() => import('./components/UserVerify'));
const EvaluationCatalog = lazyWithRetry(() => import('./components/EvaluationCatalog'));
const MyEvaluations = lazyWithRetry(() => import('./components/MyEvaluations'));
const EvaluationCertificate = lazyWithRetry(() => import('./components/EvaluationCertificate'));
const AccountSettings = lazyWithRetry(() => import('./components/AccountSettings'));
const LinkedInCallback = lazyWithRetry(() => import('./components/LinkedInCallback'));
const PrivacyPolicy = lazyWithRetry(() => import('./components/PrivacyPolicy'));

const theme = createTheme(themeConfig);

// Application principale du frontend utilisateur.
// Ce composant configure le thème, l'authentification de base stockée en local,
// et les routes publiques/privées de l'application.
function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unauthorizedMessage, setUnauthorizedMessage] = useState(false);
  // UI mode functionality removed – always use default theme
  const activeTheme = theme;


  useEffect(() => {
    const token = localStorage.getItem('user_token');
    const userData = localStorage.getItem('user_data');
    if (token) {
      if (userData) {
        try {
          setUser(JSON.parse(userData));
        } catch (e) {
          localStorage.removeItem('user_data');
        }
      }

      const loadProfile = async () => {
        try {
          const response = await api.get('/users/me');
          setUser(response.data);
          localStorage.setItem('user_data', JSON.stringify(response.data));
        } catch (err) {
          console.warn('Unable to refresh user profile on startup', err);
        } finally {
          setLoading(false);
        }
      };

      loadProfile();
    } else {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const handleUnauthorized = () => {
      setUser(null);
      setUnauthorizedMessage(true);
    };
    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, []);

  useEffect(() => {
    if (!user) return;
    
    // Initial ping
    api.post('/auth/ping').catch(() => {});
    
    // Setup heartbeat ping every 60 seconds
    const intervalId = setInterval(() => {
      api.post('/auth/ping').catch(() => {});
    }, 60000);
    
    return () => clearInterval(intervalId);
  }, [user]);

  const handleLogin = (token, userData) => {
    localStorage.setItem('user_token', token);
    localStorage.setItem('user_data', JSON.stringify(userData));
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('user_token');
    localStorage.removeItem('user_data');
    setUser(null);
  };

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: themeConfig.colors.panelBackground,
          }}
        >
          <CircularProgress sx={{ color: 'white' }} />
        </Box>
      </ThemeProvider>
    );
  }


  return (
    <ThemeProvider theme={activeTheme}>
        <CssBaseline />
        <Router>
          <ErrorBoundary>
          <Suspense fallback={
            <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: themeConfig.colors.panelBackground }}>
              <CircularProgress sx={{ color: themeConfig.colors.primaryMain }} />
            </Box>
          }>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<Navigate to="/catalogue" />} />
              <Route path="/connexion" element={<UserLogin onLogin={handleLogin} />} />
              <Route path="/inscription" element={<Register onLogin={handleLogin} />} />
              <Route path="/mot-de-passe-oublie" element={<UserForgotPassword />} />
              <Route path="/reinitialiser-mot-de-passe" element={<UserResetPassword />} />
            <Route path="/verifier-email" element={<UserVerify onLogin={handleLogin} />} />
            <Route path="/verify-email" element={<UserVerify onLogin={handleLogin} />} />
              <Route path="/auth/linkedin/callback" element={<LinkedInCallback onLogin={handleLogin} />} />
              <Route path="/politique-de-confidentialite" element={<PrivacyPolicy />} />
              
              {/* Evaluation catalog (accessible to all, enhanced when logged in) */}
              <Route path="/catalogue" element={<EvaluationCatalog user={user} onLogout={handleLogout} />} />
              <Route path="/mes-evaluations" element={<MyEvaluations user={user} onLogout={handleLogout} />} />
              <Route path="/compte" element={<AccountSettings user={user} onLogout={handleLogout} onLogin={handleLogin} />} />
              
              {/* Evaluation taking */}
              <Route path="/evaluation/:quizId" element={<EvaluationTaker user={user} onLogout={handleLogout} />} />
              <Route path="/termine" element={<EvaluationComplete user={user} onLogout={handleLogout} />} />
              
              {/* Evaluation certificate view (logged in only) */}
              {user && (
                <>
                  <Route path="/certificat/:certificateId" element={<EvaluationCertificate user={user} onLogout={handleLogout} />} />
                </>
              )}
              
              {/* Fallback */}
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </Suspense>
          </ErrorBoundary>
        </Router>
        <Snackbar
          open={unauthorizedMessage}
          autoHideDuration={6000}
          onClose={() => setUnauthorizedMessage(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            onClose={() => setUnauthorizedMessage(false)} 
            severity="warning" 
            variant="filled" 
            sx={{ 
              width: '100%', 
              color: 'white',
              '& .MuiAlert-icon': { color: 'white' },
              '& .MuiIconButton-root': { color: 'white' }
            }}
          >
            Votre session a expiré. Veuillez vous reconnecter.
          </Alert>
        </Snackbar>
        <Analytics />
        <SpeedInsights />
    </ThemeProvider>
  );
}

export default App;

